import React, { useState, useEffect, useRef } from "react";
import {
  FileText,
  Download,
  ChevronRight,
  ArrowLeft,
  CheckCircle,
  XCircle,
  File,
  Eye,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { DocumentDetail } from "./DocumentDetail";
import {
  OrderStatus,
  OrderDocument,
  OrderDocumentField,
  DocumentType,
  isPlatformDocumentType,
} from "@/models";
import { Link, useParams } from "react-router-dom";
import { generateDocumentPdf } from "@/utils/pdfUtils";
import { generateTemplatedPdf } from "@/utils/templatePdfUtils";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  documentFormApi,
  orderDocumentApi,
  documentTypeApi,
  orderApi,
  documentOutputApi,
} from "@/services/api";
import { supabase } from "@/integrations/supabase/client";
import {
  ProcessingStatusIndicator,
  ProcessingState,
} from "@/components/ui/processing-status-indicator";
import { DocumentOutput } from "@/services/api/documentOutputApi";
import { normalizeOrderDocumentFields } from "@/utils/orderDocumentFields";
import { formatReleaseDate } from "@/utils/releaseDates";
import { StepCompletionPanel } from "./StepCompletionPanel";

interface RequestedDocumentsTabProps {
  documents: OrderDocument[];
  orderStatus: OrderStatus | string;
  onDownload: (docId: string) => void;
  orderDate: string;
  propertyAddress: string;
  associationName?: string;
  ipAddress?: string | null;
  onDocumentUpdate?: (updatedDocument: OrderDocument) => void;
}

export const RequestedDocumentsTab: React.FC<RequestedDocumentsTabProps> = ({
  documents: rawDocuments,
  orderStatus,
  onDownload,
  orderDate,
  propertyAddress,
  associationName,
  ipAddress,
  onDocumentUpdate,
}) => {
  // Normalize fields shape (see src/utils/orderDocumentFields.ts) so rows
  // written after 2026-03-24 (nested section-block shape) render the same as
  // older rows (flat shape).
  const documents = React.useMemo<OrderDocument[]>(
    () =>
      (rawDocuments || []).map((doc) => ({
        ...doc,
        fields: normalizeOrderDocumentFields(doc?.fields as any[]),
      })),
    [rawDocuments]
  );

  const [selectedDocument, setSelectedDocument] =
    useState<OrderDocument | null>(null);
  const [isGenerating, setIsGenerating] = useState<Record<string, boolean>>({});
  const [isUpdatingRelease, setIsUpdatingRelease] = useState<
    Record<string, boolean>
  >({});
  const [isReleasing, setIsReleasing] = useState<{ [key: number]: boolean }>({});
  const [documentTypes, setDocumentTypes] = useState<{
    [key: number]: DocumentType;
  }>({});
  const { id: orderId } = useParams();
  const { toast } = useToast();
  const { user, selectedRole } = useAuth();

  // The buyer's-progress panel is a support tool: it writes a completion
  // record on someone else's application, letting them past a gate they have
  // not cleared. Only Dommek staff see it — management companies and
  // associations get no hint that it exists. The function enforces the same
  // rule, so hiding it here is presentation, not the control.
  const isPlatformAdmin = selectedRole?.type === "platform";
  const [associationNames, setAssociationNames] = useState<{
    [key: number]: string;
  }>({});
  const [documentOutputs, setDocumentOutputs] = useState<{
    [key: number]: DocumentOutput | null;
  }>({});
  const [childOrders, setChildOrders] = useState<{ [key: number]: any[] }>({});
  const [originalDocuments, setOriginalDocuments] = useState<{
    [key: number]: any;
  }>({});

  // Helper function to generate meaningful ZIP filename
  const generateZipFilename = (
    doc: OrderDocument,
    associationName?: string,
    orderDate?: string
  ): string => {
    const date = orderDate ? new Date(orderDate) : new Date();
    const dateString = date.toISOString().split("T")[0]; // YYYY-MM-DD format

    // Start with document name or type
    let filename =
      doc.name || doc.document?.name || `Document_${doc.document_id}`;

    // Clean up the document name to be filename-safe
    filename = filename.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");

    // Add association name if available
    if (associationName) {
      const cleanAssociationName = associationName
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "_");
      filename = `${cleanAssociationName}_${filename}`;
    }

    // Add date
    filename = `${filename}_${dateString}`;

    // Ensure reasonable length (max 200 characters before .zip extension)
    if (filename.length > 200) {
      filename = filename.substring(0, 200);
    }

    return `${filename}.zip`;
  };

  useEffect(() => {
    const fetchAssociationNames = async () => {
      const names: { [key: number]: string } = {};
      for (const doc of documents) {
        if (!associationNames[doc.association_id]) {
          const { data: association, error } = await supabase
            .from("associations")
            .select("name")
            .eq("id", doc.association_id)
            .single();
          if (error) {
            console.error("Error fetching association name:", error);
            names[doc.association_id] = `Association ID: ${doc.association_id}`;
          } else {
            names[doc.association_id] =
              association?.name || `Association ID: ${doc.association_id}`;
          }
        }
      }
      setAssociationNames(names);
    };
    fetchAssociationNames();
  }, [documents]);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      const types: { [key: number]: DocumentType } = {};
      for (const doc of documents) {
        if (
          doc.document?.document_type_id &&
          !documentTypes[doc.document.document_type_id]
        ) {
          try {
            const documentType = await documentTypeApi.getById(
              doc.document.document_type_id
            );
            types[doc.document.document_type_id] = documentType;
          } catch (error) {
            console.error("Error fetching document type:", error);
          }
        }
      }
      setDocumentTypes((prev) => ({ ...prev, ...types }));
    };
    fetchDocumentTypes();
  }, [documents]);

  // Scheduled release: the order's promised date, and which documents are
  // already waiting for it.
  const [releasePlan, setReleasePlan] = useState<{
    expected: string | null;
    scheduled: { [id: number]: string };
  }>({ expected: null, scheduled: {} });
  // The row's Release asks the same question Process asks: schedule for the
  // promised date, or send now.
  const [releaseChoiceDoc, setReleaseChoiceDoc] = useState<OrderDocument | null>(null);

  useEffect(() => {
    const fetchReleasePlan = async () => {
      if (!orderId) return;
      const ids = documents.map((d) => d.id).filter(Boolean) as number[];
      const [ordRes, odsRes] = await Promise.all([
        supabase.from("orders").select("expected_delivery_date").eq("id", Number(orderId)).maybeSingle(),
        ids.length
          ? supabase.from("order_documents").select("id, scheduled_release_at, released").in("id", ids)
          : Promise.resolve({ data: [] } as any),
      ]);
      const scheduled: { [id: number]: string } = {};
      for (const row of (odsRes.data as any[]) ?? []) {
        if (row.scheduled_release_at && !row.released) scheduled[row.id] = row.scheduled_release_at;
      }
      setReleasePlan({ expected: (ordRes.data as any)?.expected_delivery_date ?? null, scheduled });
    };
    fetchReleasePlan();
  }, [documents, orderId]);

  // Fetch child orders for each document
  useEffect(() => {
    const fetchChildOrders = async () => {
      const children: { [key: number]: any[] } = {};
      for (const doc of documents) {
        try {
          // Query orders that have this document as parent
          const { data, error } = await supabase
            .from("order_documents")
            .select(
              `
              order_id,
              order:orders(
                id,
                status,
                created_at,
                customer:customers(first_name, last_name)
              )
            `
            )
            .eq("parent_id", doc.id);

          if (!error && data) {
            children[doc.id] = data.map((item) => item.order).filter(Boolean);
          }
        } catch (error) {
          console.error(
            "Error fetching child orders for document:",
            doc.id,
            error
          );
        }
      }
      setChildOrders(children);
    };

    if (documents.length > 0) {
      fetchChildOrders();
    }
  }, [documents]);

  // Fetch original document information for update documents
  useEffect(() => {
    const fetchOriginalDocuments = async () => {
      const originals: { [key: number]: any } = {};
      for (const doc of documents) {
        if (doc.parent_id) {
          try {
            // Query the original document and its order
            const { data, error } = await supabase
              .from("order_documents")
              .select(
                `
                id,
                name,
                order_id,
                order:orders(
                  id,
                  status,
                  created_at,
                  customer:customers(first_name, last_name)
                )
              `
              )
              .eq("id", doc.parent_id)
              .single();

            if (!error && data) {
              originals[doc.id] = data;
            }
          } catch (error) {
            console.error(
              "Error fetching original document:",
              doc.parent_id,
              error
            );
          }
        }
      }
      setOriginalDocuments(originals);
    };

    if (documents.length > 0) {
      fetchOriginalDocuments();
    }
  }, [documents]);

  useEffect(() => {
    const fetchDocumentOutputs = async () => {
      const outputs: { [key: number]: DocumentOutput | null } = {};
      for (const doc of documents) {
        if (doc.id && !documentOutputs[doc.id]) {
          try {
            const output = await documentOutputApi.getByOrderDocumentId(doc.id);
            outputs[doc.id] = output;
          } catch (error) {
            console.error("Error fetching document output:", error);
            outputs[doc.id] = null;
          }
        }
      }
      setDocumentOutputs((prev) => ({ ...prev, ...outputs }));
    };
    fetchDocumentOutputs();
  }, [documents]);

  const getProcessingState = (doc: OrderDocument): ProcessingState => {
    const output = documentOutputs[doc.id];
    if (!output) return "unprocessed";
    return output.processing_state as ProcessingState;
  };

  const getProcessButtonText = (doc: OrderDocument): string => {
    const state = getProcessingState(doc);
    switch (state) {
      case "unprocessed":
        return "Process";
      case "pending":
        return "Process";
      case "processed":
        return "View Document Output";
      default:
        return "Process";
    }
  };

  const handleDownloadPdf = async (doc: OrderDocument) => {
    if (!orderId) return;

    setIsGenerating((prev) => ({ ...prev, [doc.id]: true }));

    try {
      console.log("Generating PDF for document:", doc);
      console.log("Document updated_at value:", doc.updated_at);

      // Get document form data to populate field information correctly
      let formData = null;

      if (doc.document_id) {
        try {
          const forms = await documentFormApi.list({
            queryKey: ["document-forms", { document_id: doc.document_id }],
          });
          if (forms && forms.length > 0) {
            formData = forms[0];
            console.log("Document form data fetched for PDF:", formData);
          }
        } catch (error) {
          console.error("Error fetching document form for PDF:", error);
        }
      }

      // Ensure sections are properly structured
      const preparedDoc = doc;

      // Create comprehensive sections list combining all sources (same logic as DocumentDetail)
      if (preparedDoc.fields) {
        // Create maps for quick lookups
        const formSectionsMap = new Map<number, any>();
        const stepOrderMap = new Map<number, number>();

        if (formData?.form_steps) {
          formData.form_steps.forEach((step: any) => {
            (step.form_sections || []).forEach((section: any) => {
              formSectionsMap.set(section.id, section);
              stepOrderMap.set(section.id, step.step_order ?? 0);
            });
          });
        }

        const documentSectionsMap = new Map<number, any>();
        if (preparedDoc.sections) {
          preparedDoc.sections.forEach((section) => {
            documentSectionsMap.set(section.section_id, section);
          });
        }

        // Get all unique section IDs from all sources
        const allSectionIds = new Set<number>();
        formSectionsMap.forEach((_, id) => allSectionIds.add(id));
        documentSectionsMap.forEach((_, id) => allSectionIds.add(id));
        (preparedDoc.fields || []).forEach((field) => {
          if (field.section_id) allSectionIds.add(field.section_id);
        });

        // Build comprehensive sections list (matching DocumentDetail.tsx exactly)
        const comprehensiveSections: any[] = [];
        allSectionIds.forEach((sectionId) => {
          const formSection = formSectionsMap.get(sectionId);
          const documentSection = documentSectionsMap.get(sectionId);
          const hasFields = (preparedDoc.fields || []).some(
            (f) => f.section_id === sectionId
          );

          const fieldWithTitle = (preparedDoc.fields || []).find(
            (f) => f.section_id === sectionId && f.section_title
          );
          const titleFromFields = fieldWithTitle?.section_title;

          if (formSection || (documentSection && hasFields) || hasFields) {
            // Create section with proper fallback values (matching DocumentDetail.tsx exactly)
            const section = {
              ...(documentSection || {}),
              section_id: sectionId,
              title:
                formSection?.section_title ||
                titleFromFields ||
                documentSection?.title ||
                `Section ${sectionId}`,
              order: formSection?.section_order,
              step_order: stepOrderMap.get(sectionId),
              formSectionId: formSection?.id,
              formSectionData: formSection,
              fields: [],
            };

            // Populate fields for this section from the document's fields
            const sectionFields = preparedDoc.fields.filter(
              (f) => f.section_id === sectionId
            );

            if (sectionFields.length > 0) {
              // Create unique field definitions (one per field_id) instead of duplicating for each index
              const uniqueFieldIds = [
                ...new Set(sectionFields.map((f) => f.field_id)),
              ];

              section.fields = uniqueFieldIds
                .map((fieldId) => {
                  // Get the first instance of this field to use for field definition
                  const fieldInstance = sectionFields.find(
                    (f) => f.field_id === fieldId
                  ) as OrderDocumentField;
                  if (!fieldInstance) return null;

                  // Try to find field information from form data
                  let fieldLabel = `Field ${fieldId}`;
                  let fieldType = "text";
                  let fieldName = `field_${fieldId}`;

                  // First priority: field_type from the document fields (jsonb column)
                  if (fieldInstance.field_type) {
                    fieldType = fieldInstance.field_type;
                  } else {
                    // Second priority: field type from document form data
                    if (formData?.form_steps) {
                      for (const step of formData.form_steps) {
                        if (step.form_sections) {
                          for (const formSection of step.form_sections) {
                            if (
                              formSection.id === fieldInstance.section_id &&
                              formSection.form_fields
                            ) {
                              const formField = formSection.form_fields.find(
                                (f) => f.id === fieldId
                              );
                              if (formField && formField.field_type) {
                                fieldType = formField.field_type;
                                break;
                              }
                            }
                          }
                        }
                        if (fieldType !== "text") break; // Found field type, exit outer loop
                      }
                    }

                    // Third priority: fallback to checking field ID string for common patterns
                    if (fieldType === "text" && fieldId != null) {
                      const fieldIdStr = fieldId.toString();
                      if (fieldIdStr.includes("signature")) {
                        fieldType = "signature";
                      } else if (fieldIdStr.includes("initials")) {
                        fieldType = "initials";
                      } else if (fieldIdStr.includes("file")) {
                        fieldType = "file";
                      }
                    }
                  }

                  // Look for field label and name in form data
                  if (formData?.form_steps) {
                    for (const step of formData.form_steps) {
                      if (step.form_sections) {
                        for (const formSection of step.form_sections) {
                          if (
                            formSection.id === fieldInstance.section_id &&
                            formSection.form_fields
                          ) {
                            const formField = formSection.form_fields.find(
                              (f) => f.id === fieldId
                            );
                            if (formField) {
                              fieldLabel = formField.label || fieldLabel;
                              fieldName = formField.name || fieldName;
                            }
                          }
                        }
                      }
                    }
                  }

                  // Fallback to field_title from document.fields
                  if (fieldLabel === `Field ${fieldId}`) {
                    const docField = preparedDoc.fields.find(
                      (f) =>
                        f.field_id === fieldId &&
                        f.section_id === fieldInstance.section_id &&
                        f.field_title
                    );
                    if (docField && docField.field_title) {
                      fieldLabel = docField.field_title;
                    }
                  }

                  return {
                    id: fieldId,
                    field_id: fieldId,
                    label: fieldLabel,
                    name: fieldName,
                    type: fieldType,
                    value: fieldInstance.value, // This will be overridden when getting values by index
                    index: fieldInstance.index, // This will be used for reference but overridden
                    section_id: fieldInstance.section_id,
                  };
                })
                .filter(Boolean);
            }

            comprehensiveSections.push(section);
          }
        });

        // Sort sections: those in the form come first, ordered, then others.
        // This matches the exact sorting logic from DocumentDetail.tsx
        comprehensiveSections.sort((a, b) => {
          const aInForm = a.step_order !== undefined && a.order !== undefined;
          const bInForm = b.step_order !== undefined && b.order !== undefined;

          if (aInForm && bInForm) {
            if (a.step_order !== b.step_order) {
              return (a.step_order || 0) - (b.step_order || 0);
            }
            return (a.order || 0) - (b.order || 0);
          }
          if (aInForm) return -1;
          if (bInForm) return 1;
          return 0; // Keep original order for sections not in form
        });

        // Replace the document sections with our comprehensive list
        preparedDoc.sections = comprehensiveSections;
      }

      try {
        const zipBlob = await generateDocumentPdf(
          preparedDoc,
          associationNames[doc.association_id] || "",
          parseInt(orderId),
          orderDate,
          propertyAddress,
          ipAddress,
          formData
        );

        // Create download link for zip file
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = generateZipFilename(doc, associationNames[doc.association_id], orderDate);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Mark as downloaded if there's a processed output
        const output = documentOutputs[doc.id];
        if (output && output.processing_state === "processed") {
          try {
            await documentOutputApi.markAsDownloaded(doc.id);
            // Update local state
            setDocumentOutputs((prev) => ({
              ...prev,
              [doc.id]: {
                ...output,
                downloaded_at: new Date().toISOString(),
              },
            }));
          } catch (error) {
            console.error("Error marking as downloaded:", error);
          }
        }

        toast({
          title: "Download successful",
          description: "Document package has been downloaded successfully.",
        });
      } catch (pdfError) {
        console.error("PDF generation error:", pdfError);
        toast({
          variant: "destructive",
          title: "Error generating document",
          description: `PDF generation failed: ${
            pdfError.message || "Unknown error"
          }. Please try again or contact support.`,
        });
      }
    } catch (error) {
      console.error("Error generating document package:", error);
      toast({
        variant: "destructive",
        title: "Error generating document package",
        description:
          "There was a problem creating the document package. Please try again.",
      });
    } finally {
      setIsGenerating((prev) => ({ ...prev, [doc.id]: false }));
    }
  };

  /**
   * Release straight from the list: the saved Process data becomes the PDF
   * and the requester gets it by email. Repeatable on purpose — each release
   * mails again (Mauro, 2 Sep).
   */
  const handleReleaseDocument = async (doc: OrderDocument, mode: "now" | "schedule" = "now") => {
    if (!doc.id) return;

    const output = documentOutputs[doc.id];
    if (!output?.output_data?.sections) {
      toast({
        variant: "destructive",
        title: "Nothing to release yet",
        description: "Open Process document and save it once first.",
      });
      return;
    }

    setIsReleasing((prev) => ({ ...prev, [doc.id]: true }));
    try {
      let pdfBase64: string | null = null;
      try {
        const { pdfBlob } = await generateTemplatedPdf({
          orderDocument: { ...doc, remaining_balance: 0 } as any,
          processedSections: output.output_data.sections,
          user: user as any,
        });
        const bytes = [...new Uint8Array(await pdfBlob.arrayBuffer())];
        pdfBase64 = btoa(bytes.map((b) => String.fromCharCode(b)).join(""));
      } catch (pdfError) {
        console.error("Error generating the PDF for release:", pdfError);
      }

      const scheduledAt = releasePlan.scheduled[doc.id];
      const expected = releasePlan.expected;
      // Schedule only when asked to AND the promised date is still ahead; a
      // late release (the date already passed) always goes out immediately.
      const releaseLater =
        mode === "schedule" &&
        !scheduledAt && !!expected && expected > new Date().toISOString().slice(0, 10);

      const updatedDocument = await orderDocumentApi.updateReleaseStatus(
        doc.id,
        true,
        pdfBase64,
        releaseLater
          ? { scheduledFor: `${expected}T14:00:00Z` }
          : scheduledAt
            ? { useScheduledPdf: true }
            : undefined
      );

      if (releaseLater) {
        setReleasePlan((prev) => ({
          ...prev,
          scheduled: { ...prev.scheduled, [doc.id]: `${expected}T14:00:00Z` },
        }));
        toast({
          title: "Release scheduled",
          description: `To be released on ${formatReleaseDate(expected)} — the promised delivery date. Use Release now to send it earlier.`,
        });
      } else {
        setReleasePlan((prev) => {
          const scheduled = { ...prev.scheduled };
          delete scheduled[doc.id];
          return { ...prev, scheduled };
        });
        toast({
          title: "Document released",
          description: "The requester has been emailed the document.",
        });
      }

      if (onDocumentUpdate) {
        onDocumentUpdate(updatedDocument);
      }
    } catch (error) {
      console.error("Error releasing document:", error);
      toast({
        variant: "destructive",
        title: "Release failed",
        description: "The document could not be released. Please try again.",
      });
    } finally {
      setIsReleasing((prev) => ({ ...prev, [doc.id]: false }));
    }
  };

  const handleReleaseToggle = async (doc: OrderDocument) => {
    if (!doc.id) return;

    setIsUpdatingRelease((prev) => ({ ...prev, [doc.id]: true }));

    try {
      const newReleaseStatus = !doc.released;
      const updatedDocument = await orderDocumentApi.updateReleaseStatus(
        doc.id,
        newReleaseStatus
      );

      toast({
        title: newReleaseStatus ? "Document released" : "Document unreleased",
        description: `The document has been ${
          newReleaseStatus ? "released" : "unreleased"
        } successfully.`,
      });

      // Update the document in the parent component if callback is provided
      if (onDocumentUpdate) {
        onDocumentUpdate(updatedDocument);
      }
    } catch (error) {
      console.error("Error updating document release status:", error);
      toast({
        variant: "destructive",
        title: "Error updating release status",
        description:
          "There was a problem updating the document release status. Please try again.",
      });
    } finally {
      setIsUpdatingRelease((prev) => ({ ...prev, [doc.id]: false }));
    }
  };

  const getDocumentStatusDisplay = (doc: OrderDocument): string => {
    // For file-sourced documents, show the document status
    const documentType = doc.document?.document_type_id
      ? documentTypes[doc.document.document_type_id]
      : null;

    if (documentType?.source === "file") {
      // Try to get the actual document status if available
      // The document status could be in doc.document.status or similar
      if (doc.document && "status" in doc.document) {
        const status = (doc.document as any).status;
        // Capitalize first letter and return the status
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      }

      // Fallback to determine status based on available information
      if (doc.uploaded_file_path) {
        return "Available";
      } else {
        return "Pending";
      }
    }

    return "";
  };

  if (!documents || documents.length === 0) {
    return (
      <div className="text-center py-8">
        <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium">No documents requested</h3>
        <p className="text-sm text-muted-foreground mb-4">
          This order doesn't have any requested documents.
        </p>
      </div>
    );
  }

  if (selectedDocument) {
    // Make sure sections and fields have section_id information
    if (selectedDocument.sections && selectedDocument.fields) {
      selectedDocument.sections.forEach((section) => {
        if (section.fields) {
          section.fields.forEach((field) => {
            // Ensure section_id is set on each field
            field.section_id = section.section_id;
          });
        }
      });
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedDocument(null)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Documents
          </Button>
        </div>
        <DocumentDetail
          document={selectedDocument}
          associationName={associationName}
          ipAddress={ipAddress}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {documents.map((doc) => {
        const documentType = doc.document?.document_type_id
          ? documentTypes[doc.document.document_type_id]
          : null;

        // "Release" only means something for documents that produce an output
        // to hand over: an uploaded file, or a platform document that
        // generates one. A platform document that is merely FILLED (a buyer/
        // tenant application) has nothing to release — showing "Not Released"
        // and a release toggle there was the confusion Mauro flagged (9-Sep).
        const releaseApplies =
          documentType?.source === "file" ||
          (documentType?.source === "platform" &&
            !!documentType?.generates_output_document);

        return (
          <div key={doc.id} className="p-4 border rounded-lg hover:bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3 flex-1">
                <FileText className="h-5 w-5 text-blue-600" />
                <div className="flex flex-col gap-1 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {doc.document?.name || `Document #${doc.document_id}`}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({associationNames[doc.association_id] || "Loading..."})
                    </span>
                    {releaseApplies && (
                      <Badge
                        variant={doc.released ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {doc.released ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Released
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3 mr-1" />
                            Not Released
                          </>
                        )}
                      </Badge>
                    )}
                    {doc.uploaded_file_path && (
                      <Badge variant="outline" className="text-xs">
                        <File className="h-3 w-3 mr-1" />
                        File Uploaded
                      </Badge>
                    )}
                    {doc.parent_id && (
                      <Badge variant="secondary" className="text-xs">
                        Update Document
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Action Buttons - Updated logic based on document type */}
                <div className="flex items-center gap-2">
                  {/* File-sourced documents: Show release status and document status only, no actions */}
                  {documentType?.source === "file" && (
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={doc.released ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {doc.released ? "Released" : "Not Released"}
                      </Badge>
                      {!doc.released && (
                        <Badge variant="outline" className="text-xs">
                          Status: {getDocumentStatusDisplay(doc)}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Platform-sourced documents with output generation */}
                  {documentType?.source === "platform" &&
                    documentType?.generates_output_document && (
                      <div className="flex items-center gap-2">
                        {releasePlan.scheduled[doc.id] && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            To be released on{" "}
                            <strong className="font-semibold">
                              {formatReleaseDate(releasePlan.scheduled[doc.id])}
                            </strong>
                          </span>
                        )}
                        {/* Process it, or release it again. The processing tag
                            is gone on purpose — "tenemos la última vez que lo
                            modificaste, no suma mucho". No AI Analysis here:
                            a questionnaire is FILLED, not reviewed — its AI
                            (input review + suggestions) lives inside Process
                            (4-Sep meeting). */}
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to={`/backoffice/orders/${orderId}/documents/${doc.id}/output`}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Process document
                          </Link>
                        </Button>

                        <Button
                          size="sm"
                          disabled={isReleasing[doc.id]}
                          onClick={() => {
                            const scheduled = releasePlan.scheduled[doc.id];
                            const future =
                              !!releasePlan.expected &&
                              releasePlan.expected > new Date().toISOString().slice(0, 10);
                            // A scheduled document confirms before jumping the
                            // date; a future date offers the schedule choice;
                            // a late release just goes out.
                            if (scheduled || future) setReleaseChoiceDoc(doc);
                            else handleReleaseDocument(doc, "now");
                          }}
                        >
                          {isReleasing[doc.id] ? (
                            "Releasing..."
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              {releasePlan.scheduled[doc.id] ? "Release now" : "Release"}
                            </>
                          )}
                        </Button>

                        <AlertDialog
                          open={releaseChoiceDoc?.id === doc.id}
                          onOpenChange={(open) => {
                            if (!open) setReleaseChoiceDoc(null);
                          }}
                        >
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Release document</AlertDialogTitle>
                              <AlertDialogDescription>
                                {releasePlan.scheduled[doc.id] ? (
                                  <>
                                    This document already has a release scheduled
                                    for{" "}
                                    <strong className="font-semibold">
                                      {formatReleaseDate(releasePlan.scheduled[doc.id])}
                                    </strong>{" "}
                                    — its PDF is stored and ready. Release now
                                    emails that stored PDF immediately instead of
                                    waiting for the date.
                                  </>
                                ) : (
                                  <>
                                    The promised delivery date is{" "}
                                    <strong className="font-semibold">
                                      {formatReleaseDate(releasePlan.expected)}
                                    </strong>
                                    . Scheduling holds the email until then —
                                    releasing early teaches customers they never
                                    need Rush.
                                  </>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className={
                                  releasePlan.scheduled[doc.id]
                                    ? "bg-orange-600 hover:bg-orange-700"
                                    : undefined
                                }
                                onClick={() => {
                                  setReleaseChoiceDoc(null);
                                  handleReleaseDocument(doc, "now");
                                }}
                              >
                                Release now
                              </AlertDialogAction>
                              {!releasePlan.scheduled[doc.id] && (
                                <AlertDialogAction
                                  className="bg-orange-600 hover:bg-orange-700"
                                  onClick={() => {
                                    setReleaseChoiceDoc(null);
                                    handleReleaseDocument(doc, "schedule");
                                  }}
                                >
                                  Schedule for {formatReleaseDate(releasePlan.expected)}
                                </AlertDialogAction>
                              )}
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}

                  {/* Platform-sourced documents without output generation */}
                  {documentType?.source === "platform" &&
                    !documentType?.generates_output_document && (
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to={`/backoffice/orders/${orderId}/documents/${doc.id}`}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </Link>
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isGenerating[doc.id]}
                          onClick={() => handleDownloadPdf(doc)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {isGenerating[doc.id] ? "Preparing..." : "Download"}
                        </Button>

                        {/* No release control here: a platform document that
                            doesn't generate an output has nothing to release
                            (9-Sep, Mauro). View + Download only. */}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Support view: how far the buyer actually got, and the escape
                hatch when their answers are in but the order never recorded
                the steps. Platform documents only — an uploaded file has no
                steps to walk — and Dommek staff only. */}
            {isPlatformAdmin && documentType?.source === "platform" && (
              <StepCompletionPanel
                orderDocumentId={doc.id}
                documentName={doc.document?.name}
              />
            )}

            {/* Original Document Section for Update Documents */}
            {doc.parent_id && originalDocuments[doc.id] && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md border-t border-blue-200">
                <h4 className="text-sm font-medium mb-2 text-blue-800">
                  Original Document
                </h4>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <FileText className="h-4 w-4" />
                      <span>Update for: {originalDocuments[doc.id].name}</span>
                    </div>
                    <div className="text-xs text-blue-600">
                      Original Order: #{originalDocuments[doc.id].order_id} -{" "}
                      {originalDocuments[doc.id].order?.status}
                      {originalDocuments[doc.id].order?.customer && (
                        <span className="ml-1">
                          ({originalDocuments[doc.id].order.customer.first_name}{" "}
                          {originalDocuments[doc.id].order.customer.last_name})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link
                        to={`/backoffice/orders/${
                          originalDocuments[doc.id].order_id
                        }/documents/${doc.parent_id}`}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View Original
                      </Link>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link
                        to={`/backoffice/orders/${
                          originalDocuments[doc.id].order_id
                        }`}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        View Order
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Child Orders Section */}
            {childOrders[doc.id] && childOrders[doc.id].length > 0 && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md border-t">
                <h4 className="text-sm font-medium mb-2">Update Orders</h4>
                <div className="space-y-2">
                  {childOrders[doc.id].map((childOrder: any) => (
                    <div
                      key={childOrder.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          Order #{childOrder.id}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                          {childOrder.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {new Date(childOrder.created_at).toLocaleDateString()}
                        </span>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/backoffice/orders/${childOrder.id}`}>
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
